import { useV2GuidesControllerFetchGuides, type V2GuideListResponseDto } from "@everon-it/api-client/contents-gw";
import Button from "@components/atoms/Button";
import ContentBox from "@components/atoms/ContentBox";
import Table from "@components/atoms/Table";
import ExcelButton from "@components/molecules/ExcelButton";
import Filter from "@components/molecules/Filter";
import { PrivateRoutes } from "@constants/urls";
import useGlobalModal from "@hooks/useGlobalModal";
import { usePagination } from "@hooks/usePagination";
import useRouter from "@hooks/useRouter";
import { colors, typo } from "@styles/index";
import { excelDownload } from "@utils/excel-download";
import { Pagination } from "antd";
import { useFormik } from "formik";
import React from "react";
import styled from "styled-components";
import { ChargerDescriptionData, type ChargerDescriptionInitialValues } from "./Data";

type ChargerDescriptionTableListDataType = Omit<V2GuideListResponseDto, "createdAt" | "category" | "displayOrder"> & {
    rowNumber: number;
};

const ChargerDescriptionManagement: React.FC = () => {
    // Data
    const filter = ChargerDescriptionData.forms;
    const formInitialValues = ChargerDescriptionData.formInitialValues;
    const tableContentsInfoData = ChargerDescriptionData.tableList;

    // Props
    const MAIN_TITLE = "충전기 사용방법 관리";
    const TABLE_TITLE = "충전기 사용방법 목록";
    const FILTER_GIRD_COLUMN = 2;
    const tableButtonText = "충전기 사용방법 등록";

    // Hooks
    const DETAIL_PATH = PrivateRoutes.ChargerDescriptionDetail;
    const CREATE_PATH = PrivateRoutes.ChargerDescriptionCreate;

    const { push } = useRouter();
    const { showManualOrderManagementModal } = useGlobalModal();
    const { page, pageSize, setPage, handlePageSizeChange, resetPage, getRowNumber, setSearchParams, searchParams } = usePagination({ syncWithUrl: true });

    // URL 파라미터에서 초기값 읽기
    const getInitialValues = (): ChargerDescriptionInitialValues => ({
        title: searchParams.get("title") ?? formInitialValues.title,
        isVisible: searchParams.get("isVisible") ?? formInitialValues.isVisible,
        category: formInitialValues.category,
    });

    // Formik
    const formik = useFormik<ChargerDescriptionInitialValues>({
        initialValues: getInitialValues(),
        enableReinitialize: true,
        onSubmit: values => {
            const filteredData: Record<string, string> = {};
            Object.entries(values).forEach(([key, value]) => {
                if (value !== "") {
                    filteredData[key] = String(value);
                }
            });
            setSearchParams(filteredData);
            resetPage();
        },
    });

    // 검색 필터 초기화
    const handleFormReset = () => {
        setSearchParams({});
        resetPage();
        formik.resetForm();
    };

    // api-client: 충전기 사용방법 목록 조회
    const { data } = useV2GuidesControllerFetchGuides(
        {
            category: formik.values.category,
            title: formik.values.title || undefined,
            isVisible: formik.values.isVisible ? formik.values.isVisible === "true" : undefined,
            page,
            pageSize,
        },
        {
            query: {
                retry: false,
                refetchOnWindowFocus: false,
            },
        },
    );

    const listData: V2GuideListResponseDto[] = data?.rows ?? [];
    const totalCount = data?.count ?? 0;

    /** 작성 페이지 이동 */
    const handleCreateClick = () => {
        push(CREATE_PATH);
    };

    /** 상세 페이지 이동 uuid */
    const handleDetailClick = (uuid: number | string) => {
        push(`${DETAIL_PATH}/${uuid}`);
    };

    const ChargerDescriptionTableListData: ChargerDescriptionTableListDataType[] = listData.map((item, index) => ({
        ...item,
        rowNumber: getRowNumber(index),
    }));

    const onExcelDownload = () => {
        const header = ["제목", "노출 상태", "업데이트 일시", "관리자명"];

        // 엑셀 너비 지정
        const colsWidth = [200, 100, 150, 150];

        const excelData = listData.map(data => {
            return {
                title: data.title,
                isVisible: data.isVisible ? "노출" : "비노출",
                updatedAt: data.updatedAt,
                updatedBy: data.updatedBy,
            };
        });

        excelDownload({
            fileName: "충전기 사용방법 관리",
            header,
            data: excelData,
            colsWidth,
        });
    };

    return (
        <ContentsWrap>
            <MainHeaderTitle>{MAIN_TITLE}</MainHeaderTitle>
            <ContentBox>
                <Filter formik={formik} filterData={filter} girdColumn={FILTER_GIRD_COLUMN} formInitialValues={formInitialValues} handleFormReset={handleFormReset} />
            </ContentBox>
            <ContentBoxWithHeader>
                <TableHeader>
                    <TableTitle>{TABLE_TITLE}</TableTitle>
                    <HeaderButtonWrap>
                        <Button
                            buttonSize="small"
                            buttonType="line_black"
                            text="콘텐츠 순번관리"
                            onClick={() => {
                                if (ChargerDescriptionTableListData.length > 0) {
                                    showManualOrderManagementModal({
                                        modalHeaderTitle: "콘텐츠 순번관리",
                                        contentTitle: "콘텐츠 노출 목록",
                                        firstButtonText: "닫기",
                                        secondButtonText: "저장",
                                        category: formik.values.category,
                                    });
                                }
                            }}
                        />
                        <ExcelButton onClick={() => onExcelDownload()} />
                        <Button buttonSize="small" text={tableButtonText} onClick={handleCreateClick} />
                    </HeaderButtonWrap>
                </TableHeader>
                <Table<ChargerDescriptionTableListDataType> rowData={ChargerDescriptionTableListData} tableContentsInfoData={tableContentsInfoData} currentPageSize={pageSize} onDetailClick={handleDetailClick} />
                <TableBottomWrap>
                    <ContentsTotal>
                        총<span>{totalCount}</span>
                    </ContentsTotal>
                    <PageNationWrap>
                        <Pagination
                            defaultCurrent={page}
                            defaultPageSize={pageSize}
                            total={totalCount}
                            showSizeChanger
                            locale={{ items_per_page: "개씩 보기" }}
                            current={page}
                            pageSize={pageSize}
                            onShowSizeChange={handlePageSizeChange}
                            onChange={setPage}
                        />
                    </PageNationWrap>
                </TableBottomWrap>
            </ContentBoxWithHeader>
        </ContentsWrap>
    );
};

export default ChargerDescriptionManagement;

const ContentsWrap = styled.div`
    background-color: ${colors.GRAY11};
    height: 100%;
    padding: 40px;
    overflow: auto;
`;

const MainHeaderTitle = styled.h1`
    ${typo.DISPLAY_3}
    color: ${colors.GRAY2};
    margin-bottom: 24px;
`;

const ContentBoxWithHeader = styled.div`
    margin-top: 20px;
    padding: 22px 20px;
    background: ${colors.WHITE};
    border-radius: 16px;
    border: 1px solid ${colors.GRAY9};
`;

const TableHeader = styled.div`
    display: flex;
    justify-content: space-between;
`;

const HeaderButtonWrap = styled.div`
    display: flex;
    max-height: 32px;
    gap: 8px;
`;

const TableTitle = styled.p`
    color: ${colors.GRAY2};
    ${typo.HEADING_4};
    margin-bottom: 18px;
`;

const TableBottomWrap = styled.div`
    display: flex;

    align-items: center;
    justify-content: space-between;
    margin-top: 16px;
`;

const ContentsTotal = styled.span`
    display: flex;
    gap: 8px;
    color: ${colors.GRAY6};
    font-weight: 500;
    ${typo.BODY_8};

    span {
        ${typo.BODY_8};
        font-weight: 500;
        color: ${colors.GRAY2};
    }
`;

const PageNationWrap = styled.div`
    display: flex;
    align-items: center;
    gap: 16px;
`;
